import PropTypes from 'prop-types';

import styled, { ThemeProvider } from 'styled-components';

import { Header as HeaderComponent } from '@codex-applications/header'; // eslint-disable-line import/no-extraneous-dependencies
import themes from '@codex/themes';

import { getCookie, setCookie } from '@codex/helpers';
import Configuration from '../../configuration/index';
import { cdnUrl, siteUrl } from '../../utils/url/url';

import { selectedSuggestion } from '../../modules/ListingPage/analytics/pageAndProductsAnalytics';

const HeaderWrapper = styled.div`
  position: relative;
  z-index: 3;
  font-family: CurrysSans, sans-serif;

  /* overriding default styles from fo-app*/
  li {
    box-sizing: content-box;
    & > a {
      box-sizing: content-box;
      &:hover {
        color: #4c12a1;
      }
    }
  }

  .button,
  button {
    margin-top: 0;
  }

  @media ${({ theme }) => theme.breakpoints.touch} {
    position: sticky;
    top: 0;
  }
`;

const accountMenuLinks = {
  orders: {
    link: siteUrl('/s/my-account.html#/orders'),
    label: 'Your orders'
  },
  savedForLater: {
    link: siteUrl('/s/my-account.html#/saved-for-later'),
    label: 'Saved for later'
  },
  details: {
    link: siteUrl('/s/my-account.html#/manage-your-details/personal-details'),
    label: 'Manage your details'
  },
  stayInTouch: {
    link: siteUrl('/s/my-account.html#/:preferences'),
    label: 'Staying in touch'
  },
  findOutMore: {
    link: siteUrl('/s/my-account.html#/:questions'),
    label: 'Find out more'
  },
  signOut: {
    link: siteUrl('/logout/index.html'),
    label: 'Sign out'
  }
};

const Header = (props) => {
  if (props.hide) {
    return null;
  }

  const currentTheme = Configuration.get('app_theme');
  const desktopLogoSrc = cdnUrl(`/img/${currentTheme}_logo.png`);
  const handHeldLogoSrc = cdnUrl(`/img/${currentTheme}_logo_negative.png`);

  const searchProductVariables = {
    placeholder: 'Product name or item no.',
    btnTitle: 'Search',
    // TODO: props.searchAction is used here temporary in order to use old and new search listing page altogether
    searchAction: props.searchAction || Configuration.get('searchAction')
  };

  const getSuggestionsWithFallbackEndpoint = async (query, autocompleteApi, saytApi) => {

    const usingFallbackSearchLabsURL = getCookie('search_fallback_url');
    const cdnDomainPart = 'cdn.';
    if (usingFallbackSearchLabsURL) {
      autocompleteApi = autocompleteApi.replace(cdnDomainPart, '');
      saytApi = saytApi.replace(cdnDomainPart, '');
    }

    const getClosestStoreNumber = () => localStorage.getItem('closestStoreNumber');
    const storeNumber = parseInt(getClosestStoreNumber(), 10);

    const getData = (url) => fetch(url)
      .then((res) => res.json())
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn(`SearchLabs for URL ${url} fetch error: ${error}`);
        if (url.includes(cdnDomainPart)) {
          url = url.replace(cdnDomainPart, '');
          // eslint-disable-next-line no-console
          console.info(`Trying to query searchLabsAPI with fallback URL ${url}`);
          // fallback expires in 1 day
          setCookie('search_fallback_url', 'true', 1);
          getData(url);
        }
      });

    const [terms = [], sayt = { Results: [] }] = await Promise.all([
      getData(`${autocompleteApi}?query=${query}&max_results=5&beginning=1`),
      getData(`${saytApi}?q=${query}&do=sayt${storeNumber ? `&store_number=${storeNumber}` : ''}`)
    ]);

    return [
      ...terms.map((title) => ({ type: 'term', title })),
      ...sayt.Results.map((p) => ({ type: 'sayt', ...p }))
    ];
  };

  return (
    <HeaderWrapper>
      <ThemeProvider theme={themes.brightworld}>
        <HeaderComponent
          initialNavigation={props.initialNavigation}
          useOnlyInitialNavigation={props.useOnlyInitialNavigation}
          logoVariables={{
            desktopLogoSrc: desktopLogoSrc,
            handHeldLogoSrc: handHeldLogoSrc,
            logoLink: '/#home',
            logoAlt: 'currys'
          }}
          searchProductVariables={searchProductVariables}
          autocompleteApi={Configuration.get('api.search.autocomplete')}
          saytApi={Configuration.get('api.search.products')}
          navigationApi={Configuration.get('api.navigation')}
          userLabelSignedIn={props.userLabelSignedIn}
          basketItemsCount={props.itemsInBasket}
          basketLink={siteUrl('/o/initorder/basket-confirmation.html')}
          storesLink={siteUrl('/store-finder')}
          userLinkSignedIn={siteUrl('/s/my-account.html')}
          userLinkSignedOut={siteUrl('/s/authentication.html')}
          userSignedIn={props.isLoggedIn}
          accountMenu={accountMenuLinks}
          onSubmit={props.onSubmit}
          onSuggestionSelected={(suggestion) => {
            selectedSuggestion(suggestion.type);
          }}
          fmoRedirectPopupContentUrl={
            `${Configuration.get(
              'api.fragmentRenderers'
            )}/content-renderer?fragmentType=dynamic-content&` +
            'context={"pageType":"generic","contentArea":"fmoRedirectPopupContent"}'
          }
          // Mobile website does not have language included
          mobileSiteBaseUrl={siteUrl('/mobile', undefined, null)}
          getSuggestions={getSuggestionsWithFallbackEndpoint}
        />
      </ThemeProvider>
    </HeaderWrapper>
  );
};

Header.defaultProps = {
  isLoggedIn: false,
  itemsInBasket: 0,
  userLabelSignedIn: '',
  hide: false,
  searchAction: undefined,
  initialNavigation: undefined,
  useOnlyInitialNavigation: false,
  onSubmit: () => {
  }
};

Header.propTypes = {
  isLoggedIn: PropTypes.bool,
  itemsInBasket: PropTypes.number,
  userLabelSignedIn: PropTypes.string,
  hide: PropTypes.bool,
  searchAction: PropTypes.string,
  initialNavigation: PropTypes.shape({
    catalog: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
    }),
    pages: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
    })
  }),
  useOnlyInitialNavigation: PropTypes.bool,
  onSubmit: PropTypes.func
};

export default Header;
